import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'

const AuthContext = createContext(null)

const TOKEN_KEY = 'auth.tokens'

function getStoredTokens() {
  try {
    const raw = localStorage.getItem(TOKEN_KEY)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

function setStoredTokens(tokens) {
  if (!tokens) localStorage.removeItem(TOKEN_KEY)
  else localStorage.setItem(TOKEN_KEY, JSON.stringify(tokens))
}

async function api(path, { method = 'GET', body, token } = {}) {
  const res = await fetch(`/api/auth${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) {
    const message = data?.message || 'Request failed'
    const error = new Error(message)
    error.status = res.status
    error.data = data
    throw error
  }
  return data
}

export function AuthProvider({ children }) {
  const [tokens, setTokens] = useState(() => getStoredTokens())
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)

  const setAuth = useCallback((nextTokens, nextUser) => {
    setTokens(nextTokens)
    setStoredTokens(nextTokens)
    setUser(nextUser ?? null)
  }, [])

  const loadProfile = useCallback(async () => {
    if (!tokens?.accessToken) return
    try {
      const resp = await api('/profile', { token: tokens.accessToken, method: 'GET' })
      setUser(resp?.data?.user || null)
    } catch (e) {
      // try refresh
      try {
        if (tokens?.refreshToken) {
          const r = await api('/refresh-token', { method: 'POST', body: { refreshToken: tokens.refreshToken } })
          const newTokens = { accessToken: r.data.accessToken, refreshToken: r.data.refreshToken }
          setTokens(newTokens)
          setStoredTokens(newTokens)
          const prof = await api('/profile', { token: newTokens.accessToken, method: 'GET' })
          setUser(prof?.data?.user || null)
          return
        }
      } catch {}
      // failed, clear
      setAuth(null, null)
    }
  }, [tokens, setAuth])

  useEffect(() => {
    (async () => {
      await loadProfile()
      setLoading(false)
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const register = useCallback(async ({ username, email, password }) => {
    const r = await api('/register', { method: 'POST', body: { username, email, password } })
    const newTokens = { accessToken: r.data.accessToken, refreshToken: r.data.refreshToken }
    setAuth(newTokens, r.data.user)
    return r
  }, [setAuth])

  const login = useCallback(async ({ email, password }) => {
    const r = await api('/login', { method: 'POST', body: { email, password } })
    const newTokens = { accessToken: r.data.accessToken, refreshToken: r.data.refreshToken }
    setAuth(newTokens, r.data.user)
    return r
  }, [setAuth])

  const logout = useCallback(async () => {
    if (tokens?.accessToken) {
      try { await api('/logout', { method: 'POST', token: tokens.accessToken }) } catch {}
    }
    setAuth(null, null)
  }, [tokens, setAuth])

  const resendVerification = useCallback(async (email) => {
    return api('/resend-verification', { method: 'POST', body: { email } })
  }, [])

  const forgotPassword = useCallback(async (email) => {
    return api('/forgot-password', { method: 'POST', body: { email } })
  }, [])

  const value = useMemo(() => ({
    user, loading, tokens,
    login, register, logout, loadProfile,
    resendVerification, forgotPassword,
  }), [user, loading, tokens, login, register, logout, loadProfile, resendVerification, forgotPassword])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() { return useContext(AuthContext) }
